import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 RefreshCw,
 CheckCircle,
 XCircle,
 Clock,
 Calendar,
 Eye,
 DollarSign,
 ListChecks,
 Loader2,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminRefunds = () => {
 const navigate = useNavigate();
 const [filters, setFilters] = useState({
 status: '',
 date_from: '',
 date_to: ''
 });

 // Status options for SearchSelect
 const statusOptions = [
 { value: '', label: 'All Statuses' },
 { value: 'completed', label: 'Completed' },
 { value: 'pending', label: 'Pending' },
 { value: 'failed', label: 'Failed' },
 { value: 'cancelled', label: 'Cancelled' }
 ];

 // Clean filters to remove empty values
 const cleanFilters = Object.fromEntries(
 Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
 );

 // Build query string
 const queryString = new URLSearchParams(cleanFilters).toString();

 // Fetch all refunds for admin view
 const { data: refundsResponse, isLoading, error } = useQuery({
 queryKey: ['adminRefunds', cleanFilters],
 queryFn: async () => {
 const response = await api.get(`/admin/refunds${queryString ? `?${queryString}` : ''}`);
 return response.data;
 },
 // Fallback to payments with refund status if refunds endpoint doesn't exist
 retry: false
 });

 // Fallback: use payments with refund status if refunds endpoint doesn't exist
 const { data: paymentsResponse } = useQuery({
 queryKey: ['adminPaymentsForRefunds', cleanFilters],
 queryFn: async () => {
 const response = await api.get(`/admin/payments?status=refunded${queryString ? `&${queryString}` : ''}`);
 return response.data;
 },
 enabled: !refundsResponse && !error
 });

 const refunds = refundsResponse?.data?.data ||
 refundsResponse?.data ||
 paymentsResponse?.data?.data ||
 paymentsResponse?.data ||
 [];

 // Get status configuration
 const getStatusConfig = (status) => {
 const statusConfig = {
 completed: { color: 'bg-emerald-50 text-emerald-800', icon: CheckCircle, label: 'Completed' },
 pending: { color: 'bg-amber-50 text-amber-800', icon: Clock, label: 'Pending' },
 failed: { color: 'bg-red-50 text-red-700', icon: XCircle, label: 'Failed' },
 cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Cancelled' }
 };
 return statusConfig[status] || statusConfig.pending;
 };

 // Define table columns
 const columns = [
 {
 key: 'refund_id',
 label: 'Refund #',
 sortable: true,
 width: 'w-32',
 render: (refund) => (
 <span className="font-mono text-sm font-medium text-ink">
 #{refund.refund_id || refund.payment_reference || refund.id}
 </span>
 )
 },
 {
 key: 'payment',
 label: 'Payment',
 sortable: true,
 width: 'w-32',
 render: (refund) => (
 <span className="font-mono text-sm font-medium text-ink-secondary">
 #{refund.payment_reference || refund.payment_id || 'N/A'}
 </span>
 )
 },
 {
 key: 'booking',
 label: 'Booking',
 sortable: true,
 width: 'w-32',
 render: (refund) => (
 <button
 onClick={() => refund.booking_id && navigate(`/admin/bookings/${refund.booking_id}`)}
 className="font-mono text-sm font-medium text-ink hover:underline"
 >
 #{refund.booking?.booking_reference || refund.booking_id || 'N/A'}
 </button>
 )
 },
 {
 key: 'client',
 label: 'Client',
 sortable: true,
 width: 'w-48',
 render: (refund) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {refund.booking?.client?.name || refund.client?.name || 'Unknown Client'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {refund.booking?.client?.email || refund.client?.email || ''}
 </p>
 </div>
 )
 },
 {
 key: 'vendor',
 label: 'Vendor',
 sortable: true,
 width: 'w-48',
 render: (refund) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {refund.booking?.vendor?.business_name || 'Unknown Vendor'}
 </p>
 </div>
 )
 },
 {
 key: 'amount',
 label: 'Refund Amount',
 sortable: true,
 width: 'w-28',
 render: (refund) => {
 const formatCurrency = (value) => {
 const num = parseFloat(value);
 if (isNaN(num) || num === null || num === undefined) {
 return '0.00';
 }
 return num.toFixed(2);
 };
 return (
 <div className="text-right text-red-600">
 <p className="text-sm font-medium">
 -${formatCurrency(refund.amount || refund.refund_amount)}
 </p>
 </div>
 );
 }
 },
 {
 key: 'reason',
 label: 'Reason',
 sortable: false,
 width: 'w-48',
 render: (refund) => (
 <div className="min-w-0">
 <p className="text-sm text-ink truncate">
 {refund.reason || refund.refund_reason || 'N/A'}
 </p>
 </div>
 )
 },
 {
 key: 'created_at',
 label: 'Date',
 sortable: true,
 width: 'w-32',
 render: (refund) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(refund.created_at, 'medium')}
 </div>
 {refund.created_at && (
 <div className="text-xs text-ink-tertiary">
 {formatTime(refund.created_at)}
 </div>
 )}
 </div>
 </div>
 )
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 width: 'w-28',
 render: (refund) => {
 const statusConfig = getStatusConfig(refund.status);
 const StatusIcon = statusConfig.icon;
 return (
 <span className="whitespace-nowrap flex-shrink-0">
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
 <StatusIcon className="w-3 h-3 mr-1" />
 {statusConfig.label}
 </span>
 </span>
 );
 }
 }
 ];

 // Define table actions
 const actions = [
 {
 label: 'View Booking',
 icon: Eye,
 variant: 'default',
 onClick: (refund) => {
 if (refund.booking_id) {
 navigate(`/admin/bookings/${refund.booking_id}`);
 }
 }
 }
 ];

 // Handle search
 const handleSearch = (data, searchTerm) => {
 if (!searchTerm) return data;

 return data.filter(refund =>
 refund.refund_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.payment_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.booking?.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.booking?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.booking?.client?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 refund.refund_reason?.toLowerCase().includes(searchTerm.toLowerCase())
 );
 };

 // Handle filter changes
 const handleFilterChange = (key, value) => {
 setFilters(prev => ({
 ...prev,
 [key]: value
 }));
 };

 // Calculate totals
 const totalRefundAmount = refunds
 .filter(r => r.status === 'completed')
 .reduce((sum, r) => sum + parseFloat(r.amount || r.refund_amount || 0), 0);

 const totalRefunds = refunds.length;
 const pendingRefunds = refunds.filter(r => r.status === 'pending').length;

 if (error) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Error loading refunds</h3>
 <p className="text-red-600 text-sm">
 {error?.response?.data?.message || 'Failed to load refunds. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Refunds"
 subtitle="Manage and track all refund transactions across the platform."
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.refunds.title}
 steps={ADMIN_PAGE_GUIDES.refunds.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
 <div className="rounded-lg border border-border bg-surface p-4 sm:p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Refunds</p>
 <p className="text-2xl font-bold text-ink mt-1">{totalRefunds}</p>
 </div>
 <RefreshCw className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface p-4 sm:p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Pending</p>
 <p className="text-2xl font-bold text-amber-600 mt-1">{pendingRefunds}</p>
 </div>
 <Clock className="w-8 h-8 text-amber-500" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface p-4 sm:p-5 shadow-sm">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Refunded</p>
 <p className="text-2xl font-bold text-red-600 mt-1">
 ${totalRefundAmount.toFixed(2)}
 </p>
 </div>
 <DollarSign className="w-8 h-8 text-red-500" />
 </div>
 </div>
 </div>

 <div className="rounded-lg border border-border bg-surface p-4 sm:p-5 shadow-sm">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
 <SearchSelect
 label="Status"
 value={filters.status}
 onChange={(value) => handleFilterChange('status', value)}
 options={statusOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 <div className="rounded-lg border border-border bg-surface p-4 sm:p-5 shadow-sm">
 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading refunds…</p>
 </div>
 ) : (
 <DataTable
 data={refunds}
 columns={columns}
 actions={actions}
 isLoading={isLoading}
 searchPlaceholder="Search refunds by ID, payment, booking, client, or reason..."
 onSearch={handleSearch}
 emptyMessage="No refunds found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>
 );
};

export default AdminRefunds;
